import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { clearStoredClient } from "../context/ClientContext";
import { useDataContext, ENVIRONMENT_OPTIONS, type Environment, type PersonnelFilterMode } from "../context/DataContext";
import { useViewPreferences } from "../context/ViewPreferencesContext";
import { useTheme } from "../theme/ThemeContext";
import { useDevMode } from "../context/DevModeContext";
import "./Styles/SettingsPage.css";

interface PersonnelOption {
  id: string;
  label: string;
}

type UnknownRecord = Record<string, unknown>;

const pickString = (record: UnknownRecord, keys: string[]): string | null => {
  for (const key of keys) {
    const value = record[key];
    if (value === null || value === undefined) continue;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
  }
  return null;
};

export default function SettingsPage(): JSX.Element {
  const { logout, getName, getEmail } = useAuth();
  const accountName = getName() || "Account";
  const accountEmail = getEmail();
  const accountInitial = accountName.charAt(0).toUpperCase();
  const { 
    refreshData, 
    loadingState, 
    personnelRows, 
    allPersonnelRows,
    supervisorsRows,
    currentPersonnelId,
    currentEnvironment,
    setCurrentEnvironment,
    personnelFilterMode,
    setPersonnelFilterMode
  } = useDataContext();
  const { setReportsSelectedPersonnelId } = useViewPreferences();
  const { themes, currentThemeId, setTheme, signOutTheme } = useTheme();
  const { devMode } = useDevMode();
  // Use currentPersonnelId from DataContext which has fallback logic applied
  const selectedPersonnelId = currentPersonnelId;
  const isRefreshing = loadingState === "loading";
  
  // Cooldown state for refresh button (5 seconds after clicking)
  // Uses sessionStorage to persist across component re-mounts during refresh
  const COOLDOWN_KEY = "refresh_cooldown_until";
  const COOLDOWN_MS = 5000;
  
  const [refreshCooldown, setRefreshCooldown] = useState(() => {
    // Check if there's an active cooldown on mount
    const cooldownUntil = sessionStorage.getItem(COOLDOWN_KEY);
    if (cooldownUntil) {
      const remaining = parseInt(cooldownUntil, 10) - Date.now();
      return remaining > 0;
    }
    return false;
  });
  
  // On mount, check for existing cooldown and set timer for remaining time
  useEffect(() => {
    const cooldownUntil = sessionStorage.getItem(COOLDOWN_KEY);
    if (cooldownUntil) {
      const remaining = parseInt(cooldownUntil, 10) - Date.now();
      if (remaining > 0) {
        setRefreshCooldown(true);
        const timer = setTimeout(() => {
          setRefreshCooldown(false);
          sessionStorage.removeItem(COOLDOWN_KEY);
        }, remaining);
        return () => clearTimeout(timer);
      } else {
        // Cooldown expired, clean up
        sessionStorage.removeItem(COOLDOWN_KEY);
      }
    }
  }, []);
  
  // Handle refresh with cooldown
  const handleRefresh = () => {
    if (isRefreshing || refreshCooldown) return;
    
    // Set cooldown timestamp in sessionStorage
    const cooldownUntil = Date.now() + COOLDOWN_MS;
    sessionStorage.setItem(COOLDOWN_KEY, cooldownUntil.toString());
    setRefreshCooldown(true);
    
    // Start the cooldown timer
    setTimeout(() => {
      setRefreshCooldown(false);
      sessionStorage.removeItem(COOLDOWN_KEY);
    }, COOLDOWN_MS);
    
    refreshData();
  };
  
  // Get the return path from navigation state, fallback to dashboard
  // Build personnel options from data context (filtered based on mode)
  const personnelOptions = useMemo(() => {
    const options: PersonnelOption[] = personnelRows
      .map(row => {
        const id = pickString(row, ["PersonnelId", "PersonId", "Id"]);
        if (!id) return null;
        const given = pickString(row, ["GivenNames", "GivenName"]) ?? "";
        const surname = pickString(row, ["Surname", "LastName"]) ?? "";
        const label = [given, surname].filter(Boolean).join(" ").trim() || id;
        return { id, label };
      })
      .filter((option): option is PersonnelOption => Boolean(option))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    return options;
  }, [personnelRows]);

  // Searchable dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setDropdownSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [dropdownOpen]);

  // Filter dropdown options based on search
  const filteredPersonnelOptions = useMemo(() => {
    if (!dropdownSearch.trim()) return personnelOptions;
    const term = dropdownSearch.toLowerCase();
    return personnelOptions.filter(option => option.label.toLowerCase().includes(term));
  }, [personnelOptions, dropdownSearch]);

  // Get selected person label
  const selectedPersonLabel = useMemo(() => {
    const selected = personnelOptions.find(opt => opt.id === selectedPersonnelId);
    return selected?.label ?? "Select personnel...";
  }, [personnelOptions, selectedPersonnelId]);

  const handleSelectPerson = (id: string) => {
    setReportsSelectedPersonnelId(id);
    setDropdownOpen(false);
    setDropdownSearch("");
  };

  // Handle environment change with cascading updates
  const handleEnvironmentChange = (env: Environment) => {
    if (env === currentEnvironment) return;
    
    // Change environment - this will trigger data reload
    setCurrentEnvironment(env);
    
    // After environment change, check if we need to adjust filter mode and selection
    // This is handled in DataContext when the new data loads
  };

  // Handle filter mode change with selection preservation
  const handleFilterModeChange = (mode: PersonnelFilterMode) => {
    if (mode === personnelFilterMode) return;
    
    // Check if current selection exists in the new list
    const previousSelectedId = selectedPersonnelId;
    
    setPersonnelFilterMode(mode);
    
    // After changing mode, check if selection still exists
    const newList = mode === "supervisors" ? supervisorsRows : allPersonnelRows;
    const selectionExists = newList.some(row => {
      const id = pickString(row, ["PersonnelId", "PersonId", "Id"]);
      return id === previousSelectedId;
    });
    
    if (!selectionExists && newList.length > 0) {
      // Select first person in new list
      const firstPerson = pickString(newList[0], ["PersonnelId", "PersonId", "Id"]);
      if (firstPerson) {
        setReportsSelectedPersonnelId(firstPerson);
      }
    }
  };

  // Check if supervisors toggle should be disabled
  const supervisorsDisabled = supervisorsRows.length === 0;

  const handleLogout = useCallback(() => {
    signOutTheme();
    clearStoredClient();
    logout();
  }, [logout, signOutTheme]);

  return (
    <div className="settings-page">
      <div className="settings-content">
        <section className="settings-account">
          <div className="settings-account__row">
            <span className="settings-account__avatar" aria-hidden="true">{accountInitial}</span>
            <div className="settings-account__details">
              <div className="settings-account__name">{accountName}</div>
              {accountEmail && <div className="settings-account__meta">{accountEmail}</div>}
            </div>
            <button
              type="button"
              className="settings-account__logout"
              onClick={handleLogout}
              aria-label="Log out"
              title="Log out"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="currentColor" d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </svg>
              <span className="settings-account__logout-text">Log out</span>
            </button>
          </div>
          <button
            type="button"
            className={`settings-account__refresh${isRefreshing ? " settings-account__refresh--loading" : ""}`}
            onClick={handleRefresh}
            disabled={isRefreshing || refreshCooldown}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
            </svg>
            <span>{isRefreshing ? "Refreshing..." : refreshCooldown ? "Please wait..." : "Refresh Data"}</span>
          </button>
        </section>

        {/* Environment Selection - Dev Mode Only */}
        {devMode && (
          <div className="settings-section">
            <h2 className="settings-section__title">Environment</h2>
            <div className="settings-toggle-group settings-toggle-group--environment">
              {ENVIRONMENT_OPTIONS.map(opt => {
                // Split label like "Staging (Permissions)" into main and sub parts
                const match = opt.label.match(/^(.+?)\s*\((.+)\)$/);
                const mainLabel = match ? match[1] : opt.label;
                const subLabel = match ? `(${match[2]})` : null;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`settings-toggle-btn settings-toggle-btn--stacked${currentEnvironment === opt.key ? " settings-toggle-btn--active" : ""}`}
                    onClick={() => handleEnvironmentChange(opt.key)}
                  >
                    <span className="settings-toggle-btn__main">{mainLabel}</span>
                    {subLabel && <span className="settings-toggle-btn__sub">{subLabel}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Personnel Filter Selection - Dev Mode Only */}
        {devMode && (
          <div className="settings-section">
            <h2 className="settings-section__title">Personnel Filter</h2>
            <div className="settings-toggle-group settings-toggle-group--small">
              <button
                type="button"
                className={`settings-toggle-btn${personnelFilterMode === "supervisors" ? " settings-toggle-btn--active" : ""}${supervisorsDisabled ? " settings-toggle-btn--disabled" : ""}`}
                onClick={() => !supervisorsDisabled && handleFilterModeChange("supervisors")}
                disabled={supervisorsDisabled}
              >
                Supervisors
                <span className="settings-toggle-btn__count">({supervisorsRows.length})</span>
              </button>
              <button
                type="button"
                className={`settings-toggle-btn${personnelFilterMode === "all" ? " settings-toggle-btn--active" : ""}`}
                onClick={() => handleFilterModeChange("all")}
              >
                All Personnel
                <span className="settings-toggle-btn__count">({allPersonnelRows.length})</span>
              </button>
            </div>
          </div>
        )}

        {/* Personnel Selection - Dev Mode Only */}
        {devMode && (
          <div className="settings-section">
            <h2 className="settings-section__title">Personnel</h2>
            <div className="settings-personnel-select" ref={dropdownRef}>
              <button
                type="button"
                className="settings-personnel-select__trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                aria-expanded={dropdownOpen}
              >
                <span className="settings-personnel-select__text">{selectedPersonLabel}</span>
                <span className="settings-personnel-select__arrow">{dropdownOpen ? "▲" : "▼"}</span>
              </button>
              {dropdownOpen && (
                <div className="settings-personnel-select__menu">
                  <div className="settings-personnel-select__search">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      value={dropdownSearch}
                      onChange={(e) => setDropdownSearch(e.target.value)}
                      className="settings-personnel-select__search-input"
                    />
                  </div>
                  <div className="settings-personnel-select__list">
                    {filteredPersonnelOptions.length === 0 ? (
                      <div className="settings-personnel-select__empty">No matches found</div>
                    ) : (
                      filteredPersonnelOptions.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          className={`settings-personnel-select__item${option.id === selectedPersonnelId ? " settings-personnel-select__item--selected" : ""}`}
                          onClick={() => handleSelectPerson(option.id)}
                        >
                          {option.label}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="settings-section">
          <h2 className="settings-section__title">Colour Scheme</h2>
          <div className="settings-brand-select">
            {themes.map((theme) => {
              const rowSelected = currentThemeId === theme.id;
              return (
              <button
                key={theme.id}
                type="button"
                className={`settings-brand-select__option${rowSelected ? " settings-brand-select__option--selected" : ""}`}
                aria-pressed={rowSelected}
                onClick={() => setTheme(theme.id)}
              >
                <span className="settings-brand-select__name">
                  {theme.label}
                </span>
                <div className="settings-brand-select__colors" aria-hidden="true">
                  {theme.swatches.map((swatch, index) => (
                    <span
                      key={`${theme.id}-${index}`}
                      className="settings-brand-select__swatch"
                      style={{ backgroundColor: swatch }}
                    />
                  ))}
                </div>
              </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
